import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiDownload,
  FiMoreVertical,
  FiMail,
  FiEdit,
  FiTrash2,
  FiEye,
  FiAward,
  FiTrendingUp,
  FiBookOpen,
} from 'react-icons/fi';

import {
  AdminLayout,
  Card,
  Button,
  StatsCard,
  DataTable,
  SearchInput,
  SelectInput,
} from '@grcs/components';

import { StudentProfile } from '@grcs/types/DataTypes';
import { useData } from '@grcs/context/DataContext';

import {
  useClassOperations,
  useStudentOperations,
} from '@grcs/hook/useDatabase';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';

interface StudentWithStats extends StudentProfile {
  homeworkCompleted: number;
  homeworkPending: number;
  averageScore: number;
  lastActivity: string;
}

interface StudentFilters {
  search: string;
  status: 'all' | 'active' | 'inactive';
  grade: string;
  sortBy: 'name' | 'score' | 'activity';
  sortOrder: 'asc' | 'desc';
}

export const ClassStudentEnhanced: React.FC = () => {
  const { classId, year } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { supabase } = useData();

  const classOps = useClassOperations();
  const studentOps = useStudentOperations();

  const [students, setStudents] = useState<StudentWithStats[]>([]);
  const [className, setClassName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [filters, setFilters] = useState<StudentFilters>({
    search: '',
    status: 'all',
    grade: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Simulated bulk operations state
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchClassAndStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const fetchClassAndStudents = async () => {
    try {
      setIsLoading(true);

      // Get class information
      const { data: classData } = await classOps.getClassInfoByClassId(
        classId!,
      );
      const classToken = classData?.[0]?.class_token;
      setClassName(classData?.[0]?.class_name || '');

      // Get students
      const { data: studentData } = await studentOps.getStudentsByClassId(
        classToken!,
      );

      // Debug logging
      console.log('📚 Class students loaded:', {
        classToken,
        studentCount: studentData?.length || 0,
        students: studentData?.map((s) => s.email) || [],
      });

      // Fetch real homework data for each student
      const enhancedStudents: StudentWithStats[] = await Promise.all(
        (studentData || []).map(async (student: StudentProfile) => {
          try {
            // Get all homework-student relations for this student in this class
            const { data: homeworkRelations } = await supabase
              .from('homework_student_relation')
              .select('*, homework!inner(article_token)')
              .eq('student_token', student.student_token)
              .eq('class_token', classToken);

            if (!homeworkRelations || homeworkRelations.length === 0) {
              return {
                ...student,
                homeworkCompleted: 0,
                homeworkPending: 0,
                averageScore: 0,
                lastActivity: new Date().toISOString(),
              };
            }

            // Calculate metrics
            const completed = homeworkRelations.filter((hw) => hw.completed);
            const pending = homeworkRelations.filter((hw) => !hw.completed);

            // Calculate average score from completed homework
            let totalScore = 0;
            let scoredHomeworkCount = 0;

            for (const hw of completed) {
              try {
                // Get article to know question count
                const { data: article } = await supabase
                  .from('library')
                  .select('question')
                  .eq('id', hw.homework.article_token)
                  .single();

                if (article?.question && hw.question_attempted) {
                  const questions = parseQuestions(article.question);
                  const totalQuestions = questions.length;

                  if (totalQuestions > 0) {
                    const attempted = JSON.parse(hw.question_attempted);
                    const correctAnswers = Object.values(attempted).filter(
                      (isCorrect) => isCorrect === true,
                    ).length;
                    const score = Math.round(
                      (correctAnswers / totalQuestions) * 100,
                    );
                    totalScore += score;
                    scoredHomeworkCount++;
                  }
                }
              } catch (error) {
                console.error('Error calculating score for homework:', error);
              }
            }

            const averageScore =
              scoredHomeworkCount > 0
                ? Math.round(totalScore / scoredHomeworkCount)
                : 0;

            // Get last activity from most recent completed_at
            const completedDates = completed
              .filter((hw) => hw.completed_at)
              .map((hw) => new Date(hw.completed_at).getTime());
            const lastActivity =
              completedDates.length > 0
                ? new Date(Math.max(...completedDates)).toISOString()
                : new Date().toISOString();

            return {
              ...student,
              homeworkCompleted: completed.length,
              homeworkPending: pending.length,
              averageScore,
              lastActivity,
            };
          } catch (error) {
            console.error(
              `Error fetching homework for student ${student.student_token}:`,
              error,
            );
            return {
              ...student,
              homeworkCompleted: 0,
              homeworkPending: 0,
              averageScore: 0,
              lastActivity: new Date().toISOString(),
            };
          }
        }),
      );

      setStudents(enhancedStudents);
    } catch (error) {
      toast({
        title: 'Error loading class data',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredStudents = useMemo(() => {
    let filtered = students.filter((student) => {
      const matchesSearch =
        student.first_name
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        student.last_name
          ?.toLowerCase()
          .includes(filters.search.toLowerCase()) ||
        student.email?.toLowerCase().includes(filters.search.toLowerCase());

      const matchesStatus =
        filters.status === 'all' ||
        (filters.status === 'active' &&
          student.lastActivity &&
          new Date(student.lastActivity) >
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));

      return matchesSearch && matchesStatus;
    });

    // Sort students
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`;
          bValue = `${b.first_name} ${b.last_name}`;
          break;
        case 'score':
          aValue = a.averageScore;
          bValue = b.averageScore;
          break;
        case 'activity':
          aValue = new Date(a.lastActivity);
          bValue = new Date(b.lastActivity);
          break;
        default:
          return 0;
      }

      if (filters.sortOrder === 'desc') {
        return bValue > aValue ? 1 : -1;
      }
      return aValue > bValue ? 1 : -1;
    });

    return filtered;
  }, [students, filters]);

  const studentStats = useMemo(() => {
    if (students.length === 0)
      return { total: 0, active: 0, avgScore: 0, completion: 0 };

    const activeStudents = students.filter(
      (s) =>
        new Date(s.lastActivity) >
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    ).length;

    const avgScore = Math.round(
      students.reduce((sum, s) => sum + s.averageScore, 0) / students.length,
    );

    const completion = Math.round(
      (students.reduce(
        (sum, s) =>
          sum + s.homeworkCompleted / (s.homeworkCompleted + s.homeworkPending),
        0,
      ) /
        students.length) *
        100,
    );

    return {
      total: students.length,
      active: activeStudents,
      avgScore,
      completion,
    };
  }, [students]);

  const handleBulkAction = async (action: string) => {
    if (selectedStudents.length === 0) {
      toast({
        title: 'No students selected',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsProcessing(true);

    // Simulate bulk operation
    await new Promise((resolve) => setTimeout(resolve, 2000));

    toast({
      title: `${action} completed`,
      description: `Applied to ${selectedStudents.length} students`,
      status: 'success',
      duration: 3000,
    });

    setSelectedStudents([]);
    setIsProcessing(false);
  };

  const handleExportData = () => {
    const csvContent = [
      'Name,Email,Completed,Pending,Average Score,Last Activity',
      ...filteredStudents.map(
        (s) =>
          `"${s.first_name} ${s.last_name}",${s.email},${s.homeworkCompleted},${
            s.homeworkPending
          },${s.averageScore},${new Date(s.lastActivity).toLocaleDateString()}`,
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${className}_students_${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      key: 'name',
      header: 'Student',
      render: (student: StudentWithStats) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">
            {student.first_name} {student.last_name}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {student.email}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (student: StudentWithStats) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Badge colorScheme="green" variant="subtle">
              {student.homeworkCompleted} completed
            </Badge>
            {student.homeworkPending > 0 && (
              <Badge colorScheme="orange" variant="subtle">
                {student.homeworkPending} pending
              </Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.500">
            {Math.round(
              (student.homeworkCompleted /
                (student.homeworkCompleted + student.homeworkPending)) *
                100,
            )}
            % completion
          </Text>
        </VStack>
      ),
    },
    {
      key: 'performance',
      header: 'Performance',
      render: (student: StudentWithStats) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Text fontWeight="medium">{student.averageScore}%</Text>
            <Badge
              colorScheme={
                student.averageScore >= 90
                  ? 'green'
                  : student.averageScore >= 75
                  ? 'blue'
                  : 'orange'
              }
              variant="subtle"
            >
              {student.averageScore >= 90
                ? 'Excellent'
                : student.averageScore >= 75
                ? 'Good'
                : 'Needs Improvement'}
            </Badge>
          </HStack>
        </VStack>
      ),
    },
    {
      key: 'activity',
      header: 'Last Activity',
      render: (student: StudentWithStats) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm">
            {new Date(student.lastActivity).toLocaleDateString()}
          </Text>
          <Badge
            colorScheme={
              new Date(student.lastActivity) >
              new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
                ? 'green'
                : new Date(student.lastActivity) >
                  new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                ? 'yellow'
                : 'red'
            }
            variant="subtle"
          >
            {new Date(student.lastActivity) >
            new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
              ? 'Active'
              : new Date(student.lastActivity) >
                new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
              ? 'Recent'
              : 'Inactive'}
          </Badge>
        </VStack>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (student: StudentWithStats) => (
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiMoreVertical />}
            variant="ghost"
            size="sm"
          />
          <MenuList>
            <MenuItem
              icon={<FiEye />}
              onClick={() =>
                navigate(
                  `/teacher/${year}/student/${student.student_token}/analytics`,
                )
              }
            >
              View Analytics
            </MenuItem>
            <MenuItem
              icon={<FiMail />}
              onClick={() => window.open(`mailto:${student.email}`, '_blank')}
            >
              Send Email
            </MenuItem>
            <MenuItem icon={<FiEdit />}>Edit Student</MenuItem>
            <Divider />
            <MenuItem icon={<FiTrash2 />} color="red.500">
              Remove from Class
            </MenuItem>
          </MenuList>
        </Menu>
      ),
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading student data...</Text>
          </VStack>
        </Center>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            {className} Students
          </Text>
          <Text color="gray.600">
            Year {year} • Manage class enrollment and track student progress
          </Text>
        </Box>

        {/* Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title="Total Students"
            value={studentStats.total.toString()}
            icon={FiUsers}
          />
          <StatsCard
            title="Active Students"
            value={studentStats.active.toString()}
            icon={FiTrendingUp}
          />
          <StatsCard
            title="Average Score"
            value={`${studentStats.avgScore}%`}
            icon={FiAward}
          />
          <StatsCard
            title="Completion Rate"
            value={`${studentStats.completion}%`}
            icon={FiBookOpen}
          />
        </SimpleGrid>

        {/* Main Content */}
        <Card>
          <Tabs>
            <TabList>
              <Tab>All Students ({filteredStudents.length})</Tab>
              <Tab>Performance Analytics</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {/* Controls */}
                  <HStack justify="space-between" wrap="wrap" spacing={4}>
                    <HStack spacing={3}>
                      <SearchInput
                        placeholder="Search students..."
                        value={filters.search}
                        onChange={(value) =>
                          setFilters((prev) => ({ ...prev, search: value }))
                        }
                      />
                      <SelectInput
                        value={filters.status}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: value as any,
                          }))
                        }
                        options={[
                          { label: 'All Students', value: 'all' },
                          { label: 'Active Only', value: 'active' },
                          { label: 'Inactive Only', value: 'inactive' },
                        ]}
                      />
                      <SelectInput
                        value={filters.sortBy}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            sortBy: value as any,
                          }))
                        }
                        options={[
                          { label: 'Sort by Name', value: 'name' },
                          { label: 'Sort by Score', value: 'score' },
                          { label: 'Sort by Activity', value: 'activity' },
                        ]}
                      />
                    </HStack>

                    <Button
                      leftIcon={<FiDownload />}
                      variant="secondary"
                      onClick={handleExportData}
                      size="sm"
                    >
                      Export
                    </Button>
                  </HStack>

                  {/* Bulk Actions */}
                  {selectedStudents.length > 0 && (
                    <Card variant="outline" bg="blue.50">
                      <HStack justify="space-between">
                        <Text fontWeight="medium">
                          {selectedStudents.length} student(s) selected
                        </Text>
                        <HStack spacing={2}>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleBulkAction('Send Reminder')}
                            isLoading={isProcessing}
                          >
                            Send Reminder
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleBulkAction('Update Grade')}
                            isLoading={isProcessing}
                          >
                            Update Grade
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setSelectedStudents([])}
                          >
                            Clear Selection
                          </Button>
                        </HStack>
                      </HStack>
                    </Card>
                  )}

                  {/* Students Table */}
                  <DataTable data={filteredStudents} columns={columns} />
                </VStack>
              </TabPanel>

              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Text fontSize="lg" fontWeight="semibold">
                    Performance Analytics
                  </Text>

                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Top Performers */}
                    <Card>
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold">Top Performers</Text>
                        {filteredStudents
                          .sort((a, b) => b.averageScore - a.averageScore)
                          .slice(0, 5)
                          .map((student, index) => (
                            <HStack
                              key={student.student_token}
                              justify="space-between"
                            >
                              <HStack>
                                <Badge colorScheme="gold" variant="solid">
                                  #{index + 1}
                                </Badge>
                                <Text fontWeight="medium">
                                  {student.first_name} {student.last_name}
                                </Text>
                              </HStack>
                              <Text fontWeight="bold" color="green.500">
                                {student.averageScore}%
                              </Text>
                            </HStack>
                          ))}
                      </VStack>
                    </Card>

                    {/* Students Needing Help */}
                    <Card>
                      <VStack align="stretch" spacing={4}>
                        <Text fontWeight="semibold">Students Needing Help</Text>
                        {filteredStudents
                          .filter((s) => s.averageScore < 75)
                          .sort((a, b) => a.averageScore - b.averageScore)
                          .slice(0, 5)
                          .map((student) => (
                            <HStack
                              key={student.student_token}
                              justify="space-between"
                            >
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">
                                  {student.first_name} {student.last_name}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  {student.homeworkPending} pending assignments
                                </Text>
                              </VStack>
                              <Badge colorScheme="red" variant="subtle">
                                {student.averageScore}%
                              </Badge>
                            </HStack>
                          ))}
                      </VStack>
                    </Card>
                  </SimpleGrid>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      </VStack>
    </AdminLayout>
  );
};
